import type {
  AuditEngagementRow,
  ClientDraftRow,
  EvidenceRecordRow,
  FileRow,
  OrganisationMembershipRow,
  OrganisationRow,
  PresentationCurrentPointerRow,
  PresentationDocumentRow,
  PresentationMappingRow,
  ReportRow,
  ReportSectionRow,
  ReportVersionRow,
  SectionWorkflowRevisionRow,
  WorkflowEventRow,
} from "./database.types";
import type {
  PersistedAudit,
  PersistedClientDraft,
  PersistedEvidence,
  PersistedFile,
  PersistedOrganisation,
  PersistedOrganisationMembership,
  PersistedPresentationCurrentPointer,
  PersistedPresentationDocument,
  PersistedPresentationMapping,
  PersistedReport,
  PersistedReportSection,
  PersistedReportVersion,
  PersistedSectionRevision,
  RepositoryResult,
  WorkflowEvent,
} from "./contracts";
import {
  validateClientDraftPayload,
  validateEvidencePayload,
  validateSectionWorkflowPayload,
  validateVersionedJson,
} from "./validation";
import { validateSemanticPresentationDocument, validatePresentationSourceManifest } from "./supabase/presentation-codecs";
import { calculatePresentationSourceSignatureV1 } from "./supabase/presentation-source-signature";
import {
  calculateRenderingConfigurationChecksum,
  validateRendererIdentity,
} from "./supabase/presentation-rendering";
import { auditDetailsCodec, reportMetadataCodec, reportVersionMetadataCodec } from "./supabase/codecs";

const validationFailure = <T>(message: string): RepositoryResult<T> => ({
  ok: false,
  error: { code: "validation_failure", message, retryable: false },
});

export function mapOrganisationRow(row: OrganisationRow): RepositoryResult<PersistedOrganisation> {
  const details = validateVersionedJson(row.details, [1], "Organisation details");
  if (!details.ok) return validationFailure(details.error.message);
  if (details.value.schemaVersion !== row.details_schema_version) {
    return validationFailure("Organisation details schema version does not match the stored schema version");
  }
  return {
    ok: true,
    value: {
      id: row.id,
      name: row.name,
      lifecycleStatus: row.lifecycle_status,
      details: details.value,
      revision: row.revision,
      archivedAt: row.archived_at ?? undefined,
      updatedAt: row.updated_at,
    },
  };
}

export const mapOrganisationMembershipRow = (
  row: OrganisationMembershipRow,
): PersistedOrganisationMembership => ({
  id: row.id,
  organisationId: row.organisation_id,
  userId: row.user_id,
  role: row.role,
  active: row.active,
  revision: row.revision,
  updatedAt: row.updated_at,
});

export function mapAuditRow(row: AuditEngagementRow): RepositoryResult<PersistedAudit> {
  const details = auditDetailsCodec.decode(row.details, row.details_schema_version);
  if (!details.ok) return validationFailure(details.error.message);
  return {
    ok: true,
    value: {
      id: row.id,
      organisationId: row.organisation_id,
      label: row.label ?? undefined,
      auditType: row.audit_type,
      status: row.status,
      deliveryStatus: row.delivery_status,
      followUpStatus: row.follow_up_status,
      rehearsal: row.rehearsal,
      deliveredAt: row.delivered_at ?? undefined,
      deliveredReportVersionId: row.delivered_report_version_id ?? undefined,
      details: details.value,
      archivedAt: row.archived_at ?? undefined,
      revision: row.revision,
      updatedAt: row.updated_at,
    },
  };
}

export function mapReportRow(row: ReportRow): RepositoryResult<PersistedReport> {
  const metadata = reportMetadataCodec.decode(row.metadata, row.metadata_schema_version);
  if (!metadata.ok) return validationFailure(metadata.error.message);
  return { ok: true, value: {
    id: row.id,
    organisationId: row.organisation_id,
    auditEngagementId: row.audit_engagement_id ?? undefined,
    reportType: row.report_type,
    title: row.title,
    reportingPeriod: row.reporting_period,
    templateKey: row.template_key ?? undefined,
    displayedVersionId: row.displayed_version_id ?? undefined,
    currentSectionId: row.current_section_id ?? undefined,
    metadata: metadata.value,
    archivedAt: row.archived_at ?? undefined,
    revision: row.revision,
    updatedAt: row.updated_at,
  }};
}

export function mapReportVersionRow(row: ReportVersionRow): RepositoryResult<PersistedReportVersion> {
  const metadata = reportVersionMetadataCodec.decode(row.metadata, row.metadata_schema_version);
  if (!metadata.ok) return validationFailure(metadata.error.message);
  return { ok: true, value: {
    id: row.id,
    organisationId: row.organisation_id,
    reportId: row.report_id,
    versionNumber: row.version_number,
    status: row.status,
    sourceVersionId: row.source_version_id ?? undefined,
    contentChecksum: row.content_checksum ?? undefined,
    presentationSignature: row.presentation_signature ?? undefined,
    auditDate: row.audit_date ?? undefined,
    metadata: metadata.value,
    approvedAt: row.approved_at ?? undefined,
    approvedBy: row.approved_by ?? undefined,
    publishedAt: row.published_at ?? undefined,
    publishedBy: row.published_by ?? undefined,
    supersededAt: row.superseded_at ?? undefined,
    revision: row.revision,
    updatedAt: row.updated_at,
  }};
}

export const mapReportSectionRow = (row: ReportSectionRow): PersistedReportSection => ({
  id: row.id,
  organisationId: row.organisation_id,
  reportVersionId: row.report_version_id,
  sectionKey: row.section_key,
  ordinal: row.ordinal,
  heading: row.heading,
  status: row.status,
  currentWorkflowRevisionId: row.current_workflow_revision_id ?? undefined,
  currentClientDraftId: row.current_client_draft_id ?? undefined,
  revision: row.revision,
  updatedAt: row.updated_at,
});

export function mapSectionWorkflowRevisionRow(
  row: SectionWorkflowRevisionRow,
): RepositoryResult<PersistedSectionRevision> {
  const payload = validateSectionWorkflowPayload(row.payload, row.payload_schema_version);
  if (!payload.ok) return validationFailure(payload.error.message);
  return {
    ok: true,
    value: {
      id: row.id,
      organisationId: row.organisation_id,
      reportVersionId: row.report_version_id,
      reportSectionId: row.report_section_id,
      revisionNumber: row.revision_number,
      payload: payload.value,
      payloadChecksum: row.payload_checksum,
      sourceRevisionId: row.source_revision_id ?? undefined,
      createdAt: row.created_at,
      createdBy: row.created_by ?? undefined,
    },
  };
}

export function mapEvidenceRow(row: EvidenceRecordRow): RepositoryResult<PersistedEvidence> {
  const payload = validateEvidencePayload(row.payload, row.payload_schema_version);
  if (!payload.ok) return validationFailure(payload.error.message);
  return {
    ok: true,
    value: {
      id: row.id,
      organisationId: row.organisation_id,
      auditEngagementId: row.audit_engagement_id ?? undefined,
      reportSectionId: row.report_section_id ?? undefined,
      evidenceType: row.evidence_type,
      sourceType: row.source_type,
      sourceId: row.source_id ?? undefined,
      observedAt: row.observed_at ?? undefined,
      payload: payload.value,
      payloadChecksum: row.payload_checksum,
      visibility: row.visibility,
      status: row.status,
      archivedAt: row.archived_at ?? undefined,
      revision: row.revision,
      createdAt: row.created_at,
      updatedAt: row.updated_at,
    },
  };
}

export function mapClientDraftRow(row: ClientDraftRow): RepositoryResult<PersistedClientDraft> {
  const content = validateClientDraftPayload(row.content, row.content_schema_version);
  if (!content.ok) return validationFailure(content.error.message);
  return {
    ok: true,
    value: {
      id: row.id,
      organisationId: row.organisation_id,
      reportVersionId: row.report_version_id,
      reportSectionId: row.report_section_id,
      sourceWorkflowRevisionId: row.source_workflow_revision_id ?? undefined,
      predecessorDraftId: row.predecessor_draft_id ?? undefined,
      draftKind: row.draft_kind,
      content: content.value,
      contentChecksum: row.content_checksum,
      status: row.status,
      manuallyEdited: row.manually_edited,
      revision: row.revision,
      approvedAt: row.approved_at ?? undefined,
      approvedBy: row.approved_by ?? undefined,
      createdAt: row.created_at,
      updatedAt: row.updated_at,
    },
  };
}

export async function mapPresentationDocumentRow(
  row: PresentationDocumentRow,
): Promise<RepositoryResult<PersistedPresentationDocument>> {
  if (!row.source_manifest || !row.source_manifest_checksum || !row.active_mapping_set_checksum) {
    return validationFailure("Legacy presentation does not contain a Batch 3B1 source manifest.");
  }
  const document = await validateSemanticPresentationDocument(
    row.document,
    row.presentation_scope,
    row.report_section_id ?? undefined,
  );
  if (!document.ok) return document;
  if (document.value.checksum !== row.document_checksum) {
    return validationFailure("Presentation document checksum does not match its canonical content.");
  }
  const manifest = await validatePresentationSourceManifest(row.source_manifest, {
    organisationId: row.organisation_id,
    reportId: row.report_id,
    reportVersionId: row.report_version_id,
    reportSectionId: row.report_section_id ?? undefined,
  });
  if (!manifest.ok) return manifest;
  if (manifest.value.checksum !== row.source_manifest_checksum) {
    return validationFailure("Presentation source-manifest checksum does not match its canonical content.");
  }
  if (row.source_signature_version !== null && row.source_signature_version !== 1) {
    return validationFailure("Presentation source-signature version is unsupported.");
  }
  if (row.source_signature_version === 1) {
    if (!row.source_signature_payload) {
      return validationFailure("Presentation source-signature payload is missing.");
    }
    const signature = await calculatePresentationSourceSignatureV1(row.source_signature_payload);
    if (!signature.ok) return signature;
    if (signature.value.signature !== row.source_content_signature) {
      return validationFailure("Presentation source signature does not match its canonical payload.");
    }
  }
  const renderFields = [
    row.renderer_name, row.renderer_version, row.template_id, row.template_version,
    row.rendering_configuration_schema_version, row.rendering_configuration,
    row.rendering_configuration_checksum, row.render_input_source_signature,
    row.rendered_file_id, row.rendered_file_checksum, row.rendered_page_count,
    row.render_attached_at,
  ];
  const hasRender = renderFields.some((value) => value !== null);
  if (hasRender && renderFields.some((value) => value === null)) {
    return validationFailure("Presentation render attachment is incomplete.");
  }
  let rendererIdentity;
  let renderingConfiguration;
  if (hasRender) {
    const identity = validateRendererIdentity({
      rendererName: row.renderer_name,
      rendererVersion: row.renderer_version,
      templateId: row.template_id,
      templateVersion: row.template_version,
    });
    if (!identity.ok) return identity;
    rendererIdentity = identity.value;
    const configuration = await calculateRenderingConfigurationChecksum(row.rendering_configuration);
    if (!configuration.ok) return configuration;
    if (configuration.value.checksum !== row.rendering_configuration_checksum) {
      return validationFailure("Presentation rendering-configuration checksum does not match.");
    }
    renderingConfiguration = configuration.value.configuration;
  }
  return {
    ok: true,
    value: {
      id: row.id,
      organisationId: row.organisation_id,
      reportId: row.report_id,
      reportVersionId: row.report_version_id,
      reportSectionId: row.report_section_id ?? undefined,
      scope: row.presentation_scope,
      scopeKey: row.scope_key,
      predecessorPresentationId: row.predecessor_presentation_id ?? undefined,
      document: document.value.document,
      documentChecksum: row.document_checksum,
      sourceManifest: manifest.value.manifest,
      sourceManifestChecksum: row.source_manifest_checksum,
      sourceContentSignature: row.source_content_signature,
      sourceSignatureVersion: row.source_signature_version === 1 ? 1 : undefined,
      sourceSignaturePayload: row.source_signature_payload ?? undefined,
      activeMappingSetNumber: row.active_mapping_set_number,
      activeMappingSetChecksum: row.active_mapping_set_checksum,
      rendererIdentity,
      renderingConfiguration,
      renderingConfigurationChecksum: row.rendering_configuration_checksum ?? undefined,
      renderInputSourceSignature: row.render_input_source_signature ?? undefined,
      renderedFileId: row.rendered_file_id ?? undefined,
      renderedFileChecksum: row.rendered_file_checksum ?? undefined,
      renderedPageCount: row.rendered_page_count ?? undefined,
      renderAttachedAt: row.render_attached_at ?? undefined,
      status: row.status,
      approvedAt: row.approved_at ?? undefined,
      approvedBy: row.approved_by ?? undefined,
      supersededAt: row.superseded_at ?? undefined,
      supersededBy: row.superseded_by ?? undefined,
      supersededByPresentationId: row.superseded_by_presentation_id ?? undefined,
      revision: row.revision,
      createdAt: row.created_at,
      createdBy: row.created_by ?? undefined,
      updatedAt: row.updated_at,
    },
  };
}

export function mapPresentationCurrentPointerRow(
  row: PresentationCurrentPointerRow,
): PersistedPresentationCurrentPointer {
  return {
    organisationId: row.organisation_id,
    reportId: row.report_id,
    reportVersionId: row.report_version_id,
    reportSectionId: row.report_section_id ?? undefined,
    scope: row.presentation_scope,
    scopeKey: row.scope_key,
    currentDraftPresentationId: row.current_draft_presentation_id ?? undefined,
    currentApprovedPresentationId: row.current_approved_presentation_id ?? undefined,
    revision: row.revision,
    createdAt: row.created_at,
    createdBy: row.created_by ?? undefined,
    updatedAt: row.updated_at,
    updatedBy: row.updated_by ?? undefined,
  };
}

export function mapPresentationMappingRow(
  row: PresentationMappingRow,
): RepositoryResult<PersistedPresentationMapping> {
  if (!row.presentation_node_key || !row.source_ordinal || !row.source_type || !row.source_span || !row.output_span) {
    return validationFailure("Legacy presentation mapping is not a Batch 3B1 typed mapping.");
  }
  const sourceSpan = validateVersionedJson(row.source_span, [1], "Presentation source span");
  if (!sourceSpan.ok) return validationFailure(sourceSpan.error.message);
  const outputSpan = validateVersionedJson(row.output_span, [1, 2], "Presentation output span");
  if (!outputSpan.ok) return validationFailure(outputSpan.error.message);
  return {
    ok: true,
    value: {
      id: row.id,
      organisationId: row.organisation_id,
      presentationDocumentId: row.presentation_document_id,
      mappingSetNumber: row.mapping_set_number,
      presentationNodeKey: row.presentation_node_key,
      sourceOrdinal: row.source_ordinal,
      sourceType: row.source_type,
      sourceRecordId: row.source_record_id ?? undefined,
      sourceReportSectionId: row.source_report_section_id ?? undefined,
      workflowRevisionId: row.workflow_revision_id ?? undefined,
      clientDraftId: row.client_draft_id ?? undefined,
      fileId: row.file_id ?? undefined,
      sourceFieldKey: row.source_field_key ?? undefined,
      exactSourceChecksum: row.exact_source_checksum,
      substantiveSourceFingerprint: row.substantive_source_fingerprint,
      sourceSpan: sourceSpan.value,
      outputSpan: outputSpan.value,
      renderedPageNumber: row.rendered_page_number ?? undefined,
      renderedBlockKey: row.rendered_block_key ?? undefined,
      createdAt: row.created_at,
      createdBy: row.created_by ?? undefined,
    },
  };
}

export function mapWorkflowEventRow(row: WorkflowEventRow): RepositoryResult<WorkflowEvent> {
  const snapshot = validateVersionedJson(row.snapshot, [1], "Workflow event snapshot");
  if (!snapshot.ok) return validationFailure(snapshot.error.message);
  if (snapshot.value.schemaVersion !== row.snapshot_schema_version) {
    return validationFailure("Workflow event snapshot schema version does not match the stored schema version");
  }
  const metadata = validateVersionedJson(row.metadata, [1], "Workflow event metadata");
  if (!metadata.ok) return validationFailure(metadata.error.message);
  if (metadata.value.schemaVersion !== row.metadata_schema_version) {
    return validationFailure("Workflow event metadata schema version does not match the stored schema version");
  }
  return {
    ok: true,
    value: {
      id: row.id,
      organisationId: row.organisation_id,
      auditEngagementId: row.audit_engagement_id ?? undefined,
      reportId: row.report_id ?? undefined,
      reportVersionId: row.report_version_id ?? undefined,
      reportSectionId: row.report_section_id ?? undefined,
      eventType: row.event_type,
      subjectType: row.subject_type,
      subjectId: row.subject_id,
      clientDraftId: row.client_draft_id ?? undefined,
      presentationDocumentId: row.presentation_document_id ?? undefined,
      contentChecksum: row.content_checksum ?? undefined,
      presentationChecksum: row.presentation_checksum ?? undefined,
      sourceWorkflowRevisionId: row.source_workflow_revision_id ?? undefined,
      metadata: metadata.value,
      snapshot: snapshot.value,
      idempotencyKey: row.idempotency_key,
      occurredAt: row.occurred_at,
      occurredBy: row.occurred_by ?? undefined,
    },
  };
}

export const mapFileRow = (row: FileRow): PersistedFile => ({
  id: row.id,
  organisationId: row.organisation_id,
  auditEngagementId: row.audit_engagement_id ?? undefined,
  reportVersionId: row.report_version_id ?? undefined,
  reportSectionId: row.report_section_id ?? undefined,
  evidenceRecordId: row.evidence_record_id ?? undefined,
  logicalName: row.logical_name,
  originalFilename: row.original_filename,
  bucketId: row.bucket_id,
  objectPath: row.object_path,
  mimeType: row.mime_type,
  sizeBytes: row.size_bytes,
  sha256: row.sha256,
  versionNumber: row.version_number,
  predecessorFileId: row.predecessor_file_id ?? undefined,
  visibility: row.visibility,
  status: row.status,
  metadata: row.metadata,
  uploadExpiresAt: row.upload_expires_at ?? undefined,
  uploadCompletedAt: row.upload_completed_at ?? undefined,
  objectVerifiedAt: row.object_verified_at ?? undefined,
  objectEtag: row.object_etag ?? undefined,
  inspectionState: row.inspection_state,
  lastReconciledAt: row.last_reconciled_at ?? undefined,
  objectRemovedAt: row.object_removed_at ?? undefined,
  archivedAt: row.archived_at ?? undefined,
  revision: row.revision,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
});
